// Number Ownership — the PROVIDER view (super_admin / service_admin / provider_owner /
// provider_admin). NumberOwnership.tsx renders this for those roles; the master view is
// untouched.
//
// A provider moves numbers it already holds between its own organization and its
// businesses: ProviderNumberReassignController (/v1/numbers/provider). Billing (the
// tenure) follows at once and the switch label follows through the same audited push as
// the master's "Push to switch". The server enforces the tree (403 outside it, 409 for
// pool / retired numbers); this page only ever lists the tree's numbers.
//
// Deliberately absent here: acquire from the pool, release to the pool, retire, bulk push
// and rollback. Those stay with the platform admin.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Landmark, Loader2, AlertTriangle, Search, Info, RefreshCw, ArrowRightLeft, Undo2,
  History as HistoryIcon, Check, X, Server,
} from 'lucide-react';
import {
  providerNumbersService, ProviderHeldNumber, ProviderMoveResult, ProviderNumberHistory, ProviderOrg,
  ProviderTarget,
} from 'api/services/providerNumbers.service';
import type { SwitchPushMode } from 'api/services/switchPush.service';
import { formatWhen } from './numberOwnershipRules';
import { ACTION_TEXT, actionPill } from './switchPushRules';
import {
  MOVE_COPY, reassignTargets, reassignBlock, canReturn, pushSummary, tonePill, filterHeld,
} from './providerReassignRules';

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>{children}</label>
);

const ErrorLine: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-sm rounded-xl px-3 py-2 flex items-start gap-2"
       style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{text}</span>
  </div>
);

interface MoveDraft {
  number: ProviderHeldNumber;
  kind: 'reassign' | 'return';
  targetId: string;
  note: string;
}

/** Read-only: tenures (outside holders anonymised by the server) and switch pushes. */
const ProviderHistoryPanel: React.FC<{ numberId: number; providerOrgId?: string; only: 'tenures' | 'pushes' }> = ({
  numberId, providerOrgId, only,
}) => {
  const [data, setData] = useState<ProviderNumberHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    providerNumbersService.history(numberId, providerOrgId).then(r => {
      if (!live) return;
      if (r.success && r.data) setData(r.data);
      else setError(r.error || 'Could not load the history');
    });
    return () => { live = false; };
  }, [numberId, providerOrgId]);

  if (error) return <ErrorLine text={error} />;
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (only === 'pushes') {
    return (
      <div>
        <div className="text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
          Switch pushes for {data.e164} (read-only)
        </div>
        {data.pushes.length === 0 ? (
          <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>No switch pushes recorded for your organizations.</div>
        ) : (
          <div className="space-y-1.5">
            {data.pushes.map(r => (
              <div key={r.id} className="text-[12.5px] flex items-start gap-2 flex-wrap">
                <span className={actionPill(r.action)}>{ACTION_TEXT[r.action]}</span>
                <span className={r.status === 'OK' ? 'tl-pill tl-pill-success' : r.status === 'FAILED' ? 'tl-pill tl-pill-coral' : 'tl-pill'}>
                  {r.status}
                </span>
                <span className="text-gray-900 dark:text-white">
                  {(r.oldBusinessId || '∅')} → {(r.newBusinessId || '∅')}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {formatWhen(r.createdAt)} by {r.requestedBy}{r.detail ? ` · ${r.detail}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      <div className="text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
        Everyone who has held {data.e164}
      </div>
      <div className="space-y-1.5">
        {data.tenures.map((t, i) => (
          <div key={i} className="text-[12.5px] flex items-start gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white">{t.organization_name}</span>
            {t.released_at ? <span className="tl-pill">past</span> : <span className="tl-pill tl-pill-success">current holder</span>}
            <span style={{ color: 'var(--text-muted)' }}>
              {formatWhen(t.acquired_at)}{t.acquired_by ? ` by ${t.acquired_by}` : ''}
              {t.released_at ? ` → ${formatWhen(t.released_at)}${t.released_by ? ` by ${t.released_by}` : ''}` : ''}
            </span>
            {t.notes && <span className="italic" style={{ color: 'var(--text-muted)' }}>“{t.notes}”</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProviderNumberOwnership: React.FC = () => {
  const [providers, setProviders] = useState<ProviderOrg[]>([]);
  const [providerOrgId, setProviderOrgId] = useState<string>('');
  const [provider, setProvider] = useState<ProviderOrg | null>(null);
  const [mode, setMode] = useState<SwitchPushMode | null>(null);
  const [numbers, setNumbers] = useState<ProviderHeldNumber[]>([]);
  const [targets, setTargets] = useState<ProviderTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ProviderMoveResult[]>([]);

  const [query, setQuery] = useState('');
  const [holderFilter, setHolderFilter] = useState('');
  const [open, setOpen] = useState<{ id: number; only: 'tenures' | 'pushes' } | null>(null);
  const [draft, setDraft] = useState<MoveDraft | null>(null);
  const [busy, setBusy] = useState(false);

  // Which provider: the server picks when the caller administers exactly one; with several
  // it asks, so the picker appears.
  useEffect(() => {
    providerNumbersService.providers().then(r => {
      if (r.success && r.data) {
        setProviders(r.data);
        if (r.data.length >= 1) setProviderOrgId(id => id || r.data![0].id);
        else { setLoading(false); setError('You are not an admin of any provider organization.'); }
      } else { setLoading(false); setError(r.error || 'Could not load your providers'); }
    });
  }, []);

  const load = useCallback(async () => {
    if (!providerOrgId) return;
    setLoading(true);
    setError(null);
    const [m, t] = await Promise.all([
      providerNumbersService.mine(providerOrgId),
      providerNumbersService.targets(providerOrgId),
    ]);
    if (m.success && m.data) {
      setNumbers(m.data.numbers || []);
      setProvider(m.data.provider);
      setMode(m.data.switch_push_mode);
    } else setError(m.error || 'Could not load your numbers');
    if (t.success && t.data) setTargets(t.data.targets || []);
    setLoading(false);
  }, [providerOrgId]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => filterHeld(numbers, query, holderFilter), [numbers, query, holderFilter]);
  const holders = useMemo(() => {
    const m = new Map<string, string>();
    numbers.forEach(n => m.set(n.holder_organization_id, n.holder_name));
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [numbers]);

  const doMove = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    const r = draft.kind === 'return'
      ? await providerNumbersService.returnToProvider(draft.number.number_id, draft.note, providerOrgId)
      : await providerNumbersService.reassign(draft.number.number_id, draft.targetId, draft.note, providerOrgId);
    setBusy(false);
    if (r.success && r.data) {
      setResults(prev => [r.data as ProviderMoveResult, ...prev].slice(0, 10));
      setDraft(null);
      setOpen(null);
      load();
    } else setError(r.error || 'The move was refused');
  };

  const block = draft && draft.kind === 'reassign' ? reassignBlock(draft.number, draft.targetId, targets) : null;
  const providerName = provider?.name || providers.find(p => p.id === providerOrgId)?.name || 'your provider';

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Landmark className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Number ownership
          </h1>
          <p className="text-[13px] mt-1 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
            The numbers {providerName} and its businesses hold. Move one between your organizations, or
            return it to {providerName}.
          </p>
        </div>
        <div className="flex items-end gap-2">
          {providers.length > 1 && (
            <div className="min-w-[200px]">
              <Label>Provider</Label>
              <select className="tl-input" value={providerOrgId} onChange={e => setProviderOrgId(e.target.value)}>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <button className="tl-btn tl-btn-secondary" onClick={load} disabled={loading || !providerOrgId}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      <div className="tl-card" style={{ borderLeft: '3px solid var(--amber-label)' }}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--amber-label)' }} />
          <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
            <strong className="text-gray-900 dark:text-white">{MOVE_COPY}</strong>{' '}
            The new holder is billed for calls from the moment of the move. The switch label is only written
            when the new holder&rsquo;s billing already recognises it; otherwise the move is reported as
            BLOCKED and the platform admin fixes the billing patterns. Taking numbers from the pool,
            releasing them and retiring them are platform-admin actions.
            {mode && mode !== 'APPLY' && (
              <> Switch push is currently <strong>{mode.toLowerCase()}</strong> on the server, so moves
                update billing but not the switch.</>
            )}
          </div>
        </div>
      </div>

      {error && <ErrorLine text={error} />}

      {results.length > 0 && (
        <div className="tl-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12px] font-medium" style={{ color: 'var(--text-muted)' }}>Moves this session</div>
            <button className="tl-icon-btn" onClick={() => setResults([])} aria-label="Clear"><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2">
            {results.map((r, i) => {
              const s = pushSummary(r.push);
              return (
                <div key={i} className="text-[12.5px] flex items-start gap-2 flex-wrap">
                  <Check className="w-3.5 h-3.5 mt-0.5" style={{ color: 'var(--success-text, inherit)' }} />
                  <span className="font-medium text-gray-900 dark:text-white">{r.number?.e164}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{r.from?.name} → {r.to?.name}</span>
                  <span className={tonePill(s.tone)}>{s.label}</span>
                  {s.detail && <span style={{ color: 'var(--text-muted)' }}>{s.detail}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="tl-card">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label>Find a number</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <input className="tl-input pl-9" value={query} onChange={e => setQuery(e.target.value)}
                     placeholder="+9122…, a holder or a label" />
            </div>
          </div>
          <div className="min-w-[190px]">
            <Label>Held by</Label>
            <select className="tl-input" value={holderFilter} onChange={e => setHolderFilter(e.target.value)}>
              <option value="">Any of your organizations</option>
              {holders.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
        </div>
        <div className="text-[12px] mt-2" style={{ color: 'var(--text-muted)' }}>
          {loading ? 'Loading…' : `${rows.length} of ${numbers.length} numbers`}
        </div>
      </div>

      {loading ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your numbers…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Landmark className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No numbers</p>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {numbers.length === 0
              ? `Neither ${providerName} nor its businesses hold a number. Ask the platform admin to assign one.`
              : 'Clear a filter to widen the search.'}
          </p>
        </div>
      ) : (
        <div className="tl-card tl-card-flush overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[12px]" style={{ color: 'var(--text-muted)' }}>
                <th className="px-4 py-2.5 font-medium">Number</th>
                <th className="px-4 py-2.5 font-medium">Held by</th>
                <th className="px-4 py-2.5 font-medium">Since</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(n => {
                const expanded = open?.id === n.number_id ? open.only : null;
                return (
                  <React.Fragment key={n.number_id}>
                    <tr className="border-t" style={{ borderColor: 'var(--border)' }}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{n.e164}</div>
                        <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                          {n.provider_name || (n.provider_id ? `trunk #${n.provider_id}` : '')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 dark:text-white">
                          {n.holder_name}
                          {n.held_by_provider && <span className="tl-pill ml-1.5">provider</span>}
                        </div>
                        {n.business_id && (
                          <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>business id: {n.business_id}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                        {formatWhen(n.held_since)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end flex-wrap">
                          <button className="tl-btn tl-btn-secondary tl-btn-sm"
                                  disabled={reassignTargets(targets, n).length === 0}
                                  title={reassignTargets(targets, n).length === 0 ? 'No other organization can hold it' : undefined}
                                  onClick={() => setDraft({ number: n, kind: 'reassign', targetId: '', note: '' })}>
                            <ArrowRightLeft className="w-3.5 h-3.5" /> Reassign
                          </button>
                          {canReturn(n) && (
                            <button className="tl-btn tl-btn-secondary tl-btn-sm"
                                    onClick={() => setDraft({ number: n, kind: 'return', targetId: '', note: '' })}>
                              <Undo2 className="w-3.5 h-3.5" /> Return to provider
                            </button>
                          )}
                          <button className="tl-icon-btn" title="Switch history"
                                  onClick={() => setOpen(expanded === 'pushes' ? null : { id: n.number_id, only: 'pushes' })}>
                            <Server className="w-4 h-4" />
                          </button>
                          <button className="tl-icon-btn" title="Ownership history"
                                  onClick={() => setOpen(expanded === 'tenures' ? null : { id: n.number_id, only: 'tenures' })}>
                            <HistoryIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr style={{ background: 'var(--surface-raised)' }}>
                        <td colSpan={4} className="px-4 py-3">
                          <ProviderHistoryPanel numberId={n.number_id} providerOrgId={providerOrgId} only={expanded} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => !busy && setDraft(null)}>
          <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {draft.kind === 'return' ? `Return ${draft.number.e164} to ${providerName}?` : `Reassign ${draft.number.e164}`}
            </h2>
            <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
              Currently held by <strong>{draft.number.holder_name}</strong>. {MOVE_COPY}
            </p>
            <div className="space-y-3">
              {draft.kind === 'reassign' && (
                <div>
                  <Label>Move to</Label>
                  <select className="tl-input" value={draft.targetId}
                          onChange={e => setDraft({ ...draft, targetId: e.target.value })}>
                    <option value="">Select an organization…</option>
                    {reassignTargets(targets, draft.number).map(t => (
                      <option key={t.id} value={t.id}>{t.name}{t.is_provider ? ' (provider)' : ''}{t.business_id ? ` · ${t.business_id}` : ''}</option>
                    ))}
                  </select>
                  {targets.filter(t => !t.assignable).length > 0 && (
                    <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      Not offered: {targets.filter(t => !t.assignable).map(t => t.reason || t.name).join(' ')}
                    </p>
                  )}
                </div>
              )}
              <div>
                <Label>Note (optional)</Label>
                <input className="tl-input" value={draft.note} placeholder="Why — recorded on both tenures"
                       onChange={e => setDraft({ ...draft, note: e.target.value })} />
              </div>
              <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5"
                   style={{ background: 'var(--surface-raised)', color: 'var(--text-muted)' }}>
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Billing moves now; the commitment term is carried over unchanged. The switch label is then
                  updated if the new holder&rsquo;s billing recognises it — you will see the result (OK, no change,
                  or BLOCKED with the reason) as soon as the move finishes.
                </span>
              </div>
              {draft.kind === 'reassign' && draft.targetId && block && (
                <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5"
                     style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <span>{block}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="tl-btn tl-btn-secondary" onClick={() => setDraft(null)} disabled={busy}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={doMove}
                      disabled={busy || (draft.kind === 'reassign' && !!block)}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                  : draft.kind === 'return' ? <Undo2 className="w-4 h-4" /> : <ArrowRightLeft className="w-4 h-4" />}
                {draft.kind === 'return' ? 'Return to provider' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderNumberOwnership;
